import {
  LONGEVITY_AREA_ORDER,
  LongevityActivationReason,
  LongevityAreaId,
  LongevityAreaScore,
  LongevityAssessmentDetail,
  LongevityAssessmentSummary,
  LongevityCaseInfo,
  LongevityFlag,
  LongevityGrade,
  LongevityManagerCase,
  LongevityTestRow,
  ManagerCaseStatus,
} from './longevity-alerts.model';

/**
 * Lettura delle risposte degli endpoint dello staff del Longevity Index (documento "Longevity
 * Index – API per il frontend" del 16 ottobre 2026, Swagger confermato): GET /manager/cases
 * (AssessmentPageDTO) e GET/PATCH /assessments/{id} (AssessmentDetailDTO). Il blocco `result`
 * ha lo stesso formato degli endpoint della persona: come nella Web App le aree si accettano
 * come array o come oggetto, con il nome da mostrare ("Corpo", "Mente", "Longevità finanziaria",
 * "Spazio collettivo") o l'enum come chiave. Se il formato non è riconosciuto si lancia un errore
 * invece di mostrare dati sbagliati.
 */
export class LongevityFormatError extends Error {
  constructor(message: string) {
    super(`Longevity: ${message}`);
    this.name = 'LongevityFormatError';
  }
}

type Raw = Record<string, any>;

const isObject = (value: unknown): value is Raw =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const firstDefined = (obj: Raw, keys: string[]): any =>
  keys.map((key) => obj[key]).find((v) => v !== undefined && v !== null);

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toDateOrNull = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const toStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const AREA_NAME_PATTERNS: [RegExp, LongevityAreaId][] = [
  [/corp|bod|physic|fisic/, 'BODY'],
  [/ment|mind|psych|emot/, 'MIND'],
  [/fin|money|econom|wealth|denaro/, 'FINANCE'],
  [/coll|spaz|space|commun|social|relaz/, 'COMMUNITY'],
];

/** Area dal nome: enum (BODY, FINANCIAL_LONGEVITY…) o nome da mostrare (Corpo, Spazio collettivo…). */
export function areaIdFromName(name: unknown): LongevityAreaId | null {
  const normalized = String(name ?? '').toLowerCase();
  return AREA_NAME_PATTERNS.find(([pattern]) => pattern.test(normalized))?.[1] ?? null;
}

function resolveAreaId(name: unknown, position: number): LongevityAreaId {
  // Senza un nome riconoscibile vale l'ordine di `code`: Corpo, Mente, Finanza, Spazio collettivo.
  return areaIdFromName(name) ?? LONGEVITY_AREA_ORDER[position] ?? 'BODY';
}

function normalizeGrade(value: unknown): LongevityGrade {
  const grade = String(value ?? '').trim().toUpperCase();
  return grade === 'A' || grade === 'M' || grade === 'B' ? grade : '-';
}

function extractAreaEntries(result: Raw): [string | null, Raw][] {
  const container = firstDefined(result, ['areas', 'areaScores', 'scores']);
  if (Array.isArray(container)) {
    return container
      .filter(isObject)
      .map((item) => [firstDefined(item, ['area', 'key', 'id', 'name', 'code']) ?? null, item]);
  }
  const source = isObject(container) ? container : result;
  return Object.entries(source)
    .filter(([, value]) => isObject(value) && ('score' in value || 'grade' in value))
    .map(([name, value]) => [name, value as Raw]);
}

export function normalizeAreas(result: Raw): LongevityAreaScore[] {
  const found = new Map<LongevityAreaId, LongevityAreaScore>();
  extractAreaEntries(result).forEach(([name, value], position) => {
    const id = resolveAreaId(name, position);
    if (!found.has(id)) {
      const score = toNumberOrNull(value['score']);
      found.set(id, {
        area: id,
        valid: toNumberOrNull(value['valid']) ?? 0,
        applicable: toNumberOrNull(value['applicable']) ?? 0,
        score,
        grade: score === null ? '-' : normalizeGrade(value['grade']),
      });
    }
  });
  return LONGEVITY_AREA_ORDER.map(
    (id) => found.get(id) ?? { area: id, valid: 0, applicable: 0, score: null, grade: '-' as LongevityGrade }
  );
}

/** "yyyy-MM-dd HH:mm:ss" in UTC (formato di savedAt) -> Date. */
export function parseLongevityDate(value: unknown): Date {
  const text = String(value ?? '').trim();
  const utcMatch = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/.exec(text);
  return new Date(utcMatch ? `${utcMatch[1]}T${utcMatch[2]}Z` : text);
}

const CASE_STATUSES: ManagerCaseStatus[] = ['OPEN', 'TAKEN_IN_CHARGE', 'CLOSED'];

function normalizeStatus(value: unknown): ManagerCaseStatus {
  const status = String(value ?? '').trim().toUpperCase();
  const match = CASE_STATUSES.find((s) => s === status);
  if (!match) {
    throw new LongevityFormatError(`stato caso non riconosciuto: "${value}"`);
  }
  return match;
}

/** Le date del caso sono ISO senza fuso, ora del server: `new Date` le legge come locali. */
function normalizeCaseInfo(raw: Raw): LongevityCaseInfo | null {
  const caseId = toNumberOrNull(raw['caseId']);
  if (caseId === null && !raw['caseStatus']) {
    return null;
  }
  const createdAt = toDateOrNull(raw['caseCreatedAt']);
  if (caseId === null || createdAt === null) {
    throw new LongevityFormatError('caso senza id o data di apertura');
  }
  return {
    caseId,
    status: normalizeStatus(raw['caseStatus']),
    createdAt,
    takenInChargeAt: toDateOrNull(raw['takenInChargeAt']),
    closedAt: toDateOrNull(raw['closedAt']),
    takenInChargeByYou: raw['takenInChargeByYou'] === true,
  };
}

/** Una riga di GET /manager/cases: un test, con il caso solo se ne ha aperto uno. */
function normalizeTestRow(raw: unknown): LongevityTestRow {
  if (!isObject(raw) || typeof raw['id'] !== 'string') {
    throw new LongevityFormatError('elemento della lista non riconosciuto');
  }
  const requestId = toNumberOrNull(raw['requestId']);
  if (requestId === null) {
    throw new LongevityFormatError('test senza richiesta associata');
  }
  const caseInfo = normalizeCaseInfo(raw);
  return {
    id: raw['id'],
    requestId,
    operatorId: toStringOrNull(raw['operatorId']),
    savedAt: parseLongevityDate(raw['savedAt']),
    total: toNumberOrNull(raw['total']),
    code: typeof raw['code'] === 'string' ? raw['code'] : '',
    caseId: caseInfo?.caseId ?? null,
    status: caseInfo?.status ?? null,
    createdAt: caseInfo?.createdAt ?? null,
    takenInChargeAt: caseInfo?.takenInChargeAt ?? null,
    closedAt: caseInfo?.closedAt ?? null,
    takenInChargeByYou: caseInfo?.takenInChargeByYou ?? false,
  };
}

function extractPageContent(raw: unknown): unknown[] {
  if (!isObject(raw) || !Array.isArray(raw['content'])) {
    throw new LongevityFormatError('la risposta non è una pagina di test');
  }
  return raw['content'];
}

/** Tutti i test della pagina, con o senza caso (lista dei test di una richiesta). */
export function normalizeTestPage(raw: unknown): LongevityTestRow[] {
  return extractPageContent(raw).map(normalizeTestRow);
}

/** Solo i test con un caso Manager aperto (coda della pagina Avvisi), con il FamilyTutor cercato. */
export function normalizeManagerCasePage(raw: unknown, operatorName: string): LongevityManagerCase[] {
  return normalizeTestPage(raw).reduce<LongevityManagerCase[]>((cases, row) => {
    if (row.caseId !== null && row.status !== null && row.createdAt !== null) {
      cases.push({
        ...row,
        caseId: row.caseId,
        status: row.status,
        createdAt: row.createdAt,
        operator: operatorName,
      });
    }
    return cases;
  }, []);
}

function normalizeFlags(raw: unknown): LongevityFlag[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(isObject).map((item) => ({
    id: String(item['id'] ?? ''),
    kind: String(item['kind'] ?? ''),
  }));
}

/** `comment` è string[] da Swagger; per difesa si accetta anche una stringa singola. */
function normalizeComment(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((line): line is string => typeof line === 'string' && line.length > 0);
  }
  return typeof raw === 'string' && raw ? [raw] : [];
}

/** AssessmentDTO: id, richiesta, data, risultato (aree, totale, commento, piano). */
export function normalizeAssessmentSummary(raw: unknown): LongevityAssessmentSummary {
  if (!isObject(raw) || typeof raw['id'] !== 'string') {
    throw new LongevityFormatError('valutazione non riconosciuta');
  }
  const result = isObject(raw['result']) ? raw['result'] : {};
  const areas = normalizeAreas(result);
  return {
    id: raw['id'],
    requestId: toNumberOrNull(raw['requestId']) ?? 0,
    savedAt: parseLongevityDate(raw['savedAt']),
    rulesVersion: String(raw['rulesVersion'] ?? ''),
    answeredQuestions: toNumberOrNull(raw['answeredQuestions']) ?? 0,
    total: toNumberOrNull(result['total']),
    areas,
    code:
      typeof result['code'] === 'string' && result['code']
        ? result['code']
        : areas.map((a) => a.grade).join(''),
    flags: normalizeFlags(result['flags']),
    comment: normalizeComment(result['comment']),
    plan: typeof result['plan'] === 'string' ? result['plan'] : '',
  };
}

function normalizeReason(raw: unknown): LongevityActivationReason {
  if (!isObject(raw) || typeof raw['type'] !== 'string') {
    throw new LongevityFormatError('motivo di apertura non riconosciuto');
  }
  return {
    type: raw['type'],
    area: raw['area'] ? areaIdFromName(raw['area']) : null,
    questions: Array.isArray(raw['questions']) ? raw['questions'].map(String) : [],
  };
}

function normalizeAnswers(raw: unknown): Record<string, string> {
  const answers: Record<string, string> = {};
  if (isObject(raw)) {
    Object.entries(raw).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        answers[key] = String(value);
      }
    });
  }
  return answers;
}

/** AssessmentDetailDTO: risposta di GET e PATCH /assessments/{id}. */
export function normalizeAssessmentDetail(raw: unknown): LongevityAssessmentDetail {
  if (!isObject(raw) || !isObject(raw['assessment'])) {
    throw new LongevityFormatError('dettaglio della valutazione non riconosciuto');
  }
  return {
    assessment: normalizeAssessmentSummary(raw['assessment']),
    answers: normalizeAnswers(raw['answers']),
    operatorId: toStringOrNull(raw['operatorId']),
    email: toStringOrNull(raw['email']),
    caseInfo: normalizeCaseInfo(raw),
    reasons: Array.isArray(raw['reasons']) ? raw['reasons'].map(normalizeReason) : [],
  };
}
